import React, { useState, useEffect } from 'react';
import { Button, Flex, Group, Select } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { IconDownload } from '@tabler/icons-react';
import { useLazyQuery } from '@apollo/client';
import dayjs from 'dayjs';

import { GENERATE_SALES_REPORT } from '../../apollo/queries/salesReport';
import { showErrorNotification } from '../../notifications';
import {
    dateToISODateString,
    isoDateStringToDate,
} from '../../helpers/getDatesBetween';

const SOURCE_TYPE_OPTIONS = [
    { value: 'any', label: 'Any Source' },
    { value: 'MANUAL_ENTRY', label: 'Mobile App' },
    { value: 'MANUAL_IMPORT', label: 'Portal Upload' },
    { value: 'AUTOMATIC', label: 'Automatic' },
];

export function SalesReportDownloader() {
    const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
        null,
        null,
    ]);
    const [sourceType, setSourceType] = useState<string>('all');

    const [generateReport, { data, loading, error }] = useLazyQuery(
        GENERATE_SALES_REPORT,
        {
            fetchPolicy: 'network-only',
        },
    );

    useEffect(() => {
        if (data?.generateSalesReport) {
            const { buffer, fileName } = data.generateSalesReport;
            const byteCharacters = atob(buffer);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i += 1) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        }
    }, [data]);

    useEffect(() => {
        if (error) {
            showErrorNotification(
                `Failed to generate sales report ${error.message}`,
            );
        }
    }, [error]);

    const handleDownload = async () => {
        if (dateRange[0] && dateRange[1]) {
            await generateReport({
                variables: {
                    salesReportParams: {
                        startDate: dayjs(dateRange[0])
                            .startOf('day')
                            .toISOString(),
                        endDate: dayjs(dateRange[1]).endOf('day').toISOString(),
                        filterSourceType:
                            sourceType !== 'any' ? sourceType : undefined,
                    },
                },
            });
        }
    };

    return (
        <div>
            <Flex align="center" gap="lg" style={{ height: 42 }}>
                <Flex
                    align="center"
                    gap="xs"
                    style={{ height: 42, width: '100%' }}
                >
                    <DatePickerInput
                        type="range"
                        size="md"
                        radius="lg"
                        placeholder="Pick dates range"
                        value={
                            dateRange.map(dateToISODateString) as [
                                string | null,
                                string | null,
                            ]
                        }
                        onChange={val =>
                            setDateRange([
                                isoDateStringToDate(val?.[0] ?? null),
                                isoDateStringToDate(val?.[1] ?? null),
                            ])
                        }
                        clearable
                        maxDate={new Date()}
                        style={{ width: '100%' }}
                        allowSingleDateInRange
                    />
                    <Select
                        placeholder="Select source type"
                        data={SOURCE_TYPE_OPTIONS}
                        value={sourceType}
                        onChange={value => setSourceType(value || 'all')}
                        clearable={false}
                        style={{ width: 300 }}
                    />
                </Flex>
                <Group>
                    <Button
                        leftSection={<IconDownload size={16} />}
                        onClick={handleDownload}
                        loading={loading}
                        disabled={!dateRange[0] || !dateRange[1]}
                        style={{ height: 42 }}
                    >
                        Generate Report
                    </Button>
                </Group>
            </Flex>
        </div>
    );
}
